/**
 * Per-section progress — the SINGLE shared computation (P2 #3, G7).
 *
 * `computeSectionProgress` is the one source of truth for "how done is this generic
 * category section?" It returns a fraction (`filled` / `total`) PLUS the same
 * `complete` boolean that `isSectionComplete` has always returned. The client
 * `section-completion.ts` wrapper and the dashboard/accordion both feed it the same
 * plain shape, so the green-check and the "2 of 3" nudge can never drift.
 *
 * This module is PURE shared data logic: NO DB, NO React, no client-only deps. In
 * particular it does NOT resolve Category-A membership — the caller passes an already
 * resolved `isCategoryA` boolean per field (the client resolves it through the
 * fail-closed `isCategoryAField`; the server reads the DB flag). The Cat A *presence*
 * rule (a non-seeded entry with a value — the Item 21 plaintext read) is reproduced
 * here so behaviour is identical to the old `catAFieldPresent` path.
 *
 * Behaviour contract (must match the former `isSectionComplete` exactly):
 *  - Hidden sections (no green-check slot) → `null`.
 *  - No required ("starred") field → `null` (nothing to measure / claim).
 *  - Card sections (incl. About You) → all VISIBLE starred fields present; `total`/
 *    `filled` count required fields, excluding any whose `showIf` is false (s111,
 *    D-110.3 — About You's former ≥5-of-7 threshold retired: all-filled-or-declined).
 *  - List sections (table/expandable) → D-156 (s156, ruling 8): `total` = EVERY row the
 *    planner can see (touched AND untouched seeded/recommended), `filled` = those whose
 *    every non-Cat-A required field is filled or declined, `complete` = all of them.
 *    Retires the old "≥1 complete user-touched row" rule.
 *
 * See `field-display-rules.md` §3 (the rulebook; `completion-model.md` retired s191) and
 * `client/src/lib/section-completion.ts` (the wrapper).
 */
import { HIDDEN_SECTIONS } from "./constants/section-affordances";
import { isHeaderRow } from "./constants/locked-rows";
import { entryUserActioned } from "./lib/entry-provenance";
import { SCORING_EXEMPT_SECTIONS } from "./constants/scoring-exempt-sections";

// ── Plain input shape (the caller maps its cache/DB rows into this) ─────────────
export interface SectionProgressField {
  fieldKey: string;
  isRequired: boolean;
  /** RESOLVED Cat A membership — client: `isCategoryAField` (DB flag + fail-closed
   *  allowlist + password/pin type); server: the DB flag. Not resolved here. */
  isCategoryA: boolean;
  fieldType: string;
  /** RESOLVED showIf visibility (s111, D-110.3). When `true` the field's `showIf`
   *  condition is false → it doesn't render, so card completion excludes it from BOTH
   *  `total` and `filled`. Resolved by the caller (client adapter via `evaluateShowIf`),
   *  same pattern as `isCategoryA`. Absent/false → visible. Card sections only. */
  hiddenByShowIf?: boolean;
  /** s155 (D-155.1, engine fix B): a `severityOptional` required field is DROPPED from the
   *  completion denominator entirely (Cat-A-consistent) — it never blocks the green check and
   *  never shows phantom progress on an untouched section. Spec-only flag, resolved by the
   *  caller from the spec allowlist (`isSeverityOptionalField`), same plumbing as `isCategoryA`.
   *  Absent/false → normal required field. It stays "Important" on the planner lens (unchanged). */
  severityOptional?: boolean;
}

export interface SectionProgressEntry {
  fieldKey: string;
  value: string | null;
  /** Normalised to a real boolean by the caller (null → false = user-touched). */
  isSeeded: boolean;
  /** s98 per-field "I don't have this" escape valve. A declined entry counts as
   *  RESOLVED (identical to a filled value); a real value still wins when both are
   *  present. Optional — absent → false. */
  declined?: boolean;
}

export interface SectionProgressRow {
  rowId: string;
  entries: SectionProgressEntry[];
  /** 🔴 s156 (E-fix, D-156): can the PLANNER SEE this row? REQUIRED, deliberately — since
   *  D-156 untouched rows block completion, a row nobody can open must never hold a section
   *  incomplete, and a row they CAN see must always be scored. Resolved ONCE by the caller
   *  using the render-path predicates (shared/lib/row-visibility), so completion measures
   *  exactly what is drawn.
   *
   *  It is required rather than defaulted because the first cut defaulted it: the category
   *  page had the facts to resolve visibility and the dashboard did not, so the two silently
   *  disagreed — the bar counted a section complete while the page showed it unfinished. A
   *  required field makes that a compile error instead of a discrepancy someone has to notice. */
  visible: boolean;
  /** s156 Part B — required fields hidden ON THIS ROW by their `showIf`. A list section's showIf is
   *  PER ROW (a charity row hides the person-only columns; the row beside it doesn't), so exclusion
   *  has to be per row too. Resolved once in `shared/section-progress-input.ts` and handed over as
   *  data, exactly like `visible`, so both adapters exclude the same fields. */
  hiddenFieldKeys?: string[];
}

export interface SectionProgressInput {
  sectionSlug: string;
  layoutType: "card" | "expandable" | "table";
  fields: SectionProgressField[];
  /** Card sections pass ONE synthetic row holding all the card's entries. */
  rows: SectionProgressRow[];
  /** Optional section-header field (s95, Option 2): a single fact for the whole
   *  section (e.g. Organ Donor Status), rendered above the record list and stored
   *  on a dedicated `__header__` row. When set, the section's completion criterion
   *  is "is this one field filled?" — a clean 0/1 — NOT the per-row list. Supplied
   *  by the client adapter from the layout config; absent → existing behaviour. */
  headerFieldKey?: string;
  /** s100: the section is manually marked "none / doesn't apply" (client_section_na).
   *  Resolves a list section as complete. */
  manualNa?: boolean;
}

// ── Output ──────────────────────────────────────────────────────────────────────
export interface SectionProgress {
  /** The same boolean `isSectionComplete` returns today. */
  complete: boolean;
  /** Complete rows (list) or filled required fields (card / About You). */
  filled: number;
  /** User-touched rows (list) or visible required fields (card). */
  total: number;
}

/** `null` when there is nothing to measure (hidden section, or no required field). */
export type SectionProgressResult = SectionProgress | null;

/** Non-empty value (trimmed) — matches the ERS `hasEntry` / `hasValue` semantics. */
function hasValue(v: string | null | undefined): boolean {
  return !!v && v.trim() !== "";
}

/** An entry RESOLVES its field when it has a non-empty value OR the planner declined
 *  it (s98 "I don't have this"). A real value wins when both are set — `hasValue`
 *  already covers that case, so the OR is order-independent. */
function entryResolves(e: SectionProgressEntry): boolean {
  return hasValue(e.value) || e.declined === true;
}

/** Is `field` filled (or declined) within `entries`? Cat A additionally requires the
 *  matching entry itself be USER-ACTIONED (the s83 row rule / old `catAFieldPresent`);
 *  non-Cat-A fields resolve on any entry for the field — seeded VALUES count the same as
 *  typed ones (s109: D-108.7 reversed — completeness is governed by required fields, not
 *  by who entered the value; a capture-upstream seed that fills a required field marks it
 *  resolved).
 *
 *  s164 (PROVENANCE-SEMANTICS-1, D-164): the Cat-A test asks `entryUserActioned`, not
 *  `isSeeded === false`. The previous wording claimed "a declined entry is always a user
 *  action (non-seeded)" — true only as a side effect of the old writer, which stamped
 *  isSeeded:false on a decline. Decline and undo no longer touch provenance, so a Cat-A
 *  decline on a still-seeded cell now qualifies on the `declined` flag alone, which is
 *  what the D-152.8 presence-metadata decline always meant. */
export function fieldFilled(field: SectionProgressField, entries: SectionProgressEntry[]): boolean {
  if (field.isCategoryA) {
    return entries.some((e) => e.fieldKey === field.fieldKey && entryUserActioned(e) && entryResolves(e));
  }
  return entries.some((e) => e.fieldKey === field.fieldKey && entryResolves(e));
}

/** PER-RECORD-LENS-1 (s158, ruling 3): the planner lens asks the SAME question completion asks —
 *  "is this field resolved on this row?" — so it must ask it with the SAME predicate. Exported under
 *  an intention-revealing name rather than copied: a second implementation is exactly how the lens and
 *  the progress bar would start disagreeing on the one screen that shows both (D-156 E-fix, again).
 *  `fieldFilled` stays the internal name this module already uses; this is its public contract. */
export const isFieldResolvedInRow = fieldFilled;

/** 🔴 s159 (ONE RECORD, ONE NUMBER) — the required fields a row still OWES: required, non-Cat-A,
 *  not `severityOptional`, not hidden on THIS row by its `showIf`, and neither filled nor declined.
 *
 *  This is the D-156 list criterion expressed as a LIST instead of a boolean: the loop below resolves
 *  a row iff this returns empty, and the collapsed-row "N required left →" ribbon hint counts what it
 *  returns. One function, so a row's count and its green check cannot contradict each other — the
 *  ribbon previously kept its own tally and drifted (it never evaluated showIf, so a required field
 *  behind a CLOSED gate still counted as work owed, while the bar had excluded it since s156 Part B).
 *
 *  Sibling of `isFieldResolvedInRow` (s158 ruling 3) and exported for the same reason: the second
 *  implementation is always where the disagreement starts.
 *
 *  NOT the planner lens's number, deliberately — the lens counts what an executor ARTIFACT consumes,
 *  which is a different field universe in both directions (required-but-unconsumed fields like
 *  `has_care_info`; consumed-but-`severityOptional` fields dropped from this denominator). Two
 *  questions — "is this record finished?" vs "would an executor be stuck?" — two honest numbers. */
export function outstandingRequiredInRow(
  fields: SectionProgressField[],
  row: Pick<SectionProgressRow, "entries"> & { hiddenFieldKeys?: string[] },
): SectionProgressField[] {
  const hidden = row.hiddenFieldKeys?.length ? new Set(row.hiddenFieldKeys) : null;
  return fields.filter(
    (f) =>
      f.isRequired &&
      f.severityOptional !== true &&
      // 🔴 s185 (D-181.5): the Cat-A carve-out is DELETED. A Cat A required field now counts here,
      // which also changes the collapsed-row ribbon hint that reads this list. Cat A RESOLVES via
      // `fieldFilled`'s presence test (D-152.8 metadata), so this is answerable server-side even
      // though the VALUE never transits.
      !hidden?.has(f.fieldKey) &&
      !fieldFilled(f, row.entries),
  );
}

export function computeSectionProgress(input: SectionProgressInput): SectionProgressResult {
  // s156 (E-fix): drop rows the planner can't see, ONCE, here — every branch below (header,
  // card, list) then measures the same visible set. The previous cut filtered in the client
  // adapter, which meant the scoring branches silently inherited the filtering too and a
  // second caller could feed a different set entirely.
  input = { ...input, rows: input.rows.filter((r) => r.visible !== false) };
  // No own green-check / progress slot (relocated / hidden surfaces, incl. Where Are
  // They?) — parity with the former isSectionComplete HIDDEN_SECTIONS short-circuit.
  if (HIDDEN_SECTIONS.has(input.sectionSlug)) return null;

  // 🔴 s178 (A3, D-178.5) — SCORING-EXEMPT BEATS manualNa, and it must be tested FIRST.
  //
  // A2 hoisted `manualNa` to the top of this function and, in doing so, put it ABOVE the
  // scoring-exemption check that lives further down. The two rulings then contradicted each
  // other on the three password-log sections that were also manual-N/A-eligible: D-177.9b says
  // those sections carry NO completion criterion at all, while D-177.5c says a manual N/A
  // resolves a section — so marking one "doesn't apply" returned {complete, 1, 1} and put a
  // section that is not scored back into the denominator as a scored, complete one.
  //
  // Exempt wins because it is the stronger statement: "this section is not measured" is a
  // property of the SECTION, and an answer about applicability cannot re-enter something that
  // is not being scored in the first place. The eligibility is also removed at the source
  // (`section-gating.ts`, 23 → 20), so this ordering is the belt to that braces — the branch
  // must be unreachable by construction AND inert if it is ever reached again.
  if (SCORING_EXEMPT_SECTIONS.has(input.sectionSlug)) return null;

  // 🔴 s177 (A2) — MANUAL N/A RESOLVES ANY SECTION, HOISTED. "The planner said this doesn't
  // apply" is the strongest completion signal there is; it cannot depend on which scoring mode
  // the section happens to use.
  //
  // It used to live INSIDE the (now-retired, s185/D-185.1) `shaded || engaged` branch, so a
  // section with no completionMode —
  // i.e. every plain D-156 strict list section — ignored `manualNa` entirely and stayed
  // incomplete no matter what the planner said. Latent since s85 and invisible because
  // `client_section_na` was EMPTY for every client on every environment (s177 Phase 0): the
  // feature had never once been used, so its dead branch had never once been reached.
  //
  // Found the moment D-177.15 made the fixture mark two strict-mode sections N/A and neither
  // resolved. That is the whole argument for exercising an untested path rather than reasoning
  // about it — the first real use found the bug in minutes.
  if (input.manualNa) return { complete: true, filled: 1, total: 1 };

  // ── Section-header field (s95, Option 2) ─────────────────────────────────────
  // A section with a declared header field is measured by that ONE fact, not the
  // per-record list: complete iff the header field is filled (on its `__header__`
  // row, flattened in here with the rest). Clean 0/1 fraction; bypasses the per-row
  // required logic entirely so other records never drag the section "incomplete".
  if (input.headerFieldKey) {
    const hf = input.fields.find((f) => f.fieldKey === input.headerFieldKey);
    if (!hf) return null;
    const allEntries = input.rows.flatMap((r) => r.entries);
    const filled = fieldFilled(hf, allEntries) ? 1 : 0;
    return { complete: filled === 1, filled, total: 1 };
  }


  // 🔴 s177 (A2, D-177.9b) — THE PASSWORD-LOG CARVE-OUT, TIME-BOXED TO PLANNER-MAGIC-1.
  //
  // The five password-log sections keep their curated recognition checklists while every other
  // section's suggestion rows retire (D-177.9a): you do not REMEMBER your online accounts, you
  // RECOGNISE them from a list. But a checklist of accounts the planner may not have must not be
  // SCORED — that is precisely the seeded-guess scoring COMPLETION-REALITY-1 exists to end. So in
  // the interim they are a recognition aid, not an obligation: no completion criterion, no green
  // check, no nag.
  //
  // ⏳ REMOVE THIS when the onboarding interview ships. The interview replaces the checklists with
  // checkbox questions ("which of these do you use?") that create only the rows the planner
  // actually has — at which point those rows are REAL and scoring resumes automatically, with no
  // further change here. Pinned by test so the carve-out cannot outlive its reason silently.
  //
  // 🔴 s178 (A3, D-178.5): the CHECK ITSELF MOVED UP, above the `manualNa` branch — see the note
  // at the top of this function. This comment stays here because it explains WHY the exemption
  // exists and when it expires; only its position changed.

  // s155 (D-155.1, engine fix B): severityOptional required fields are DROPPED from the
  // completion denominator entirely (Cat-A-consistent) — they never block the green check
  // and never show phantom progress on an untouched section. They stay "Important" on the
  // planner lens (unchanged). Only the remaining blocking-required fields are measured.
  const requiredFields = input.fields.filter((f) => f.isRequired && f.severityOptional !== true);
  // No blocking-required field → no completion criterion → nothing to measure.
  if (requiredFields.length === 0) return null;

  // ── Card (single-instance, incl. About You): all VISIBLE starred fields present ──
  // s111 (D-110.3): all-filled-or-declined — About You's former ≥5-of-7 threshold is
  // retired, so About You now uses this uniform card rule. showIf-aware: a required
  // field whose `showIf` is false doesn't render, so it's excluded from total+filled
  // (resolved by the caller, like isCategoryA). All required hidden → nothing to measure.
  if (input.layoutType === "card") {
    const cardEntries = input.rows.flatMap((r) => r.entries);
    // 🔴 s185 (D-181.5): the A-3 (s114) Cat-A carve-out is DELETED — one rule governs completion.
    // A Cat A required field is now in the denominator and RESOLVES through `fieldFilled`'s
    // presence test (D-152.8): the value never transits, but the entry's presence metadata does.
    // PYA is the only card section with Cat A required fields. 🔴 Essential #1 keeps its OWN
    // 3-non-Cat-A-field rule (`essentials-spec.ts`) — that is a separate selection and is NOT
    // changed here, so the PYA section pill and the essential may now answer differently.
    const visibleRequired = requiredFields.filter((f) => !f.hiddenByShowIf);
    if (visibleRequired.length === 0) return null;
    const total = visibleRequired.length;
    const filled = visibleRequired.filter((f) => fieldFilled(f, cardEntries)).length;
    return { complete: filled === total, filled, total };
  }

  // ── List (table / expandable) — D-156 (s156, ruling 8) ───────────────────────
  // The old rule was "≥1 complete user-touched row": a section went green on the first
  // finished record while every other row the planner had started, and every recommended
  // row they hadn't, sat unfinished behind a green check. Three changes, ruled together:
  //
  //  (a) EVERY user-touched row must be fully resolved — starting a second record can now
  //      take a section back out of green, which is the honest reading of a green check.
  //  (b) UNTOUCHED seeded/recommended rows block too, until each is filled or declined.
  //      The section banner promises these rows are recommended for everyone; until now
  //      completion ignored them, so the promise wasn't true. `declined` is the escape —
  //      "I don't have this" resolves a row exactly as a value does, so nothing is a trap.
  //  (c) 🔴 SUPERSEDED s185 (D-181.5). Cat A required fields USED to drop out of the denominator.
  //      That exemption is deleted: one rule governs completion, and a Cat A field resolves on its
  //      presence metadata (D-152.8) like any other required field.
  //      🔴 s185 (D-185.1): the 'shaded' mode that used to run above is RETIRED — every list
  //      section, including the 16 that declared it, is measured by this rule.
  //      🔴 s185 (D-182.4): a VISIBLE row resolves only when EVERY required field on it resolves —
  //      which is what the per-row `outstandingRequiredInRow` check below already enforces. The
  //      ruling's other half ("a shaded row still resolves on one") is MOOT: shaded is gone.
  //
  // 🔴 The rows measured here are the rows the PLANNER CAN SEE. The caller filters out
  // render-suppressed rows (legacy blanks / soft-hidden) before scoring — a row nobody can
  // open must never be able to hold a section incomplete forever.
  const listRequired = requiredFields;
  if (listRequired.length === 0) return null;
  // 🔴 s192 (D-192.1): a `__header__` row is a section-level fact, NEVER a record, so the
  // per-record rule must not measure it. It holds one cell (its header field) and can never
  // display the section's other required fields, so scoring it as a record made the section
  // permanently unreachable — observed on live planners in Funeral Wishes. The exclusion sits
  // HERE, below the header branch above, deliberately: that branch flattens EVERY row's entries
  // to find the header fact, so excluding header rows via `visible` (filtered at the top of this
  // function) would starve it and break the sections that are measured by their header.
  const recordRows = input.rows.filter((r) => !isHeaderRow(r.rowId));
  const total = recordRows.length;
  let filled = 0;
  for (const row of recordRows) {
    // 🔴 s156 Part B: a required field HIDDEN on this row by its showIf is excluded from this row's
    // criterion — the card branch has done this since s111, and its absence here made a charity
    // beneficiary row permanently unsatisfiable (the person-only first-name column is required,
    // correctly hidden, and can never be filled). Per ROW, because list showIf is per row.
    // Every required field hidden → nothing is being asked of this row, so it doesn't block
    // (an empty outstanding list). s159: this IS `outstandingRequiredInRow` — the ribbon hint
    // counts the same list, so the number on a row and its green check are one computation.
    if (outstandingRequiredInRow(listRequired, row).length === 0) filled += 1;
  }
  return { complete: total > 0 && filled === total, filled, total };
}
